/**
 * Trade Calculation Helpers
 * 
 * This module contains utility functions for automated trade calculations.
 * It's designed to be extensible for future calculation requirements.
 * 
 * Each calculation function handles derived field updates when one field changes.
 */

// ============= UNIVERSAL CONSTANTS =============
export const TRADE_CONSTANTS = {
  MAX_QUANTITY: 10000000,           // Max qty for quantity fields
  MAX_VALUE: 10000000000,           // Max value for value fields (qty * price)
  MIN_QUANTITY: 0,
  MIN_VALUE: 0,
};

/**
 * Calculate ending quantity based on trade quantity change
 * 
 * Formulas:
 * - For SELL: trade qty = current qty - ending qty
 *             Therefore: ending qty = current qty - trade qty
 * - For BUY: trade qty = current qty + ending qty  
 *            Therefore: ending qty = trade qty - current qty
 * 
 * @param {number} tradeQty - The new trade quantity entered by user
 * @param {number} currentQty - The current quantity holding
 * @param {string} side - 'BUY' or 'SELL'
 * @returns {number} - The calculated ending quantity
 */
export const calculateEndingQty = (tradeQty, currentQty, side) => {
  // Convert to numbers and handle edge cases
  const tqty = Number(tradeQty) || 0;
  const cqty = Number(currentQty) || 0;

  if (side === "SELL") {
    // ending qty = current qty - trade qty
    return cqty - tqty;
  } else if (side === "BUY") {
    // ending qty = trade qty + current qty
    return cqty + tqty;
  }

  // Default: return 0 if side is not recognized
  return 0;
};

/**
 * Calculate trade quantity based on ending quantity change
 * (Reverse calculation - useful if ending qty is edited)
 * 
 * Formulas:
 * - For SELL: trade qty = current qty - ending qty
 * - For BUY: trade qty = ending qty - current qty
 * 
 * @param {number} endingQty - The new ending quantity
 * @param {number} currentQty - The current quantity holding
 * @param {string} side - 'BUY' or 'SELL'
 * @returns {number} - The calculated trade quantity
 */
export const calculateTradeQty = (endingQty, currentQty, side) => {
  const eqty = Number(endingQty) || 0;
  const cqty = Number(currentQty) || 0;

  if (side === "SELL") {
    // trade qty = current qty - ending qty
    return cqty - eqty;
  } else if (side === "BUY") {
    // trade qty = ending qty - current qty
    return eqty - cqty;
  }

  return 0;
};

/**
 * Calculate trade value based on trade quantity and price
 * 
 * Formula: trade value = trade qty * ((trading price + accrued interest) / 100)
 * 
 * @param {number} tradeQty - The trade quantity
 * @param {number} currentPrice - The current trading price
 * @param {number} accruedInterest - The accrued interest
 * @returns {number} - The calculated trade value
 */
export const calculateTradeValue = (tradeQty, currentPrice, accruedInterest = 0) => {
  const tqty = Number(tradeQty) || 0;
  const price = Number(currentPrice) || 0;
  const accrued = Number(accruedInterest) || 0;
  return tqty * ((price + accrued) / 100);
};

/**
 * Calculate ending market value based on ending quantity and price
 * 
 * Formula: ending market value = ending qty * ((trading price + accrued interest) / 100)
 * 
 * @param {number} endingQty - The ending quantity
 * @param {number} currentPrice - The current trading price
 * @param {number} accruedInterest - The accrued interest
 * @returns {number} - The calculated ending market value
 */
export const calculateendingSecurityMarketValue = (endingQty, currentPrice, accruedInterest = 0) => {
  const eqty = Number(endingQty) || 0;
  const price = Number(currentPrice) || 0;
  const accrued = Number(accruedInterest) || 0;
  return eqty * ((price + accrued) / 100);
};

/**
 * Calculate trade quantity based on trade value
 * (Reverse calculation - useful if trade value is edited)
 * 
 * Formula: trade qty = trade value / ((trading price + accrued interest) / 100)
 * 
 * @param {number} tradeValue - The trade value
 * @param {number} currentPrice - The current trading price
 * @param {number} accruedInterest - The accrued interest
 * @returns {number} - The calculated trade quantity
 */
export const calculateTradeQtyFromValue = (tradeValue, currentPrice, accruedInterest = 0) => {
  const tvalue = Number(tradeValue) || 0;
  const price = Number(currentPrice) || 0;
  const accrued = Number(accruedInterest) || 0;
  const priceWithAccrued = (price + accrued) / 100;
  if (priceWithAccrued === 0) return 0;
  return tvalue / priceWithAccrued;
};

/**
 * Calculate ending quantity based on ending market value
 * (Reverse calculation - useful if ending market value is edited)
 * 
 * Formula: ending qty = ending market value / ((trading price + accrued interest) / 100)
 * 
 * @param {number} endingSecurityMarketValue - The ending market value
 * @param {number} currentPrice - The current trading price
 * @param {number} accruedInterest - The accrued interest
 * @returns {number} - The calculated ending quantity
 */
export const calculateEndingQtyFromValue = (endingSecurityMarketValue, currentPrice, accruedInterest = 0) => {
  const emvalue = Number(endingSecurityMarketValue) || 0;
  const price = Number(currentPrice) || 0;
  const accrued = Number(accruedInterest) || 0;
  const priceWithAccrued = (price + accrued) / 100;
  if (priceWithAccrued === 0) return 0;
  return emvalue / priceWithAccrued;
};

/**
 * Update a trade row with automatically calculated fields
 * 
 * This is the main function to use when any editable field changes.
 * It applies all necessary calculations and returns the updated row object.
 * 
 * Supports 4 editable fields:
 * - tradeQuantity: Updates endingQuantity, tradeValue, endingSecurityMarketValue
 * - endingQuantity: Updates tradeQuantity, tradeValue, endingSecurityMarketValue
 * - tradeValue: Updates tradeQuantity, endingQuantity, endingSecurityMarketValue
 * - endingSecurityMarketValue: Updates endingQuantity, tradeQuantity, tradeValue
 * 
 * @param {Object} tradeRow - The trade row object containing tradeQuantity, currentQuantity, side, currentPrice, etc.
 * @param {string} changedField - The field that was changed ('tradeQuantity', 'endingQuantity', 'tradeValue', or 'endingSecurityMarketValue')
 * @param {*} newValue - The new value for the changed field
 * @returns {Object} - Updated trade row object with recalculated fields
 */
export const updateTradeRowCalculations = (tradeRow, changedField, newValue) => {
  if (!tradeRow) return tradeRow;

  const updatedRow = { ...tradeRow };
  const currentPrice = updatedRow.currentPrice || 0;
  const currentQty = updatedRow.currentQuantity || 0;
  const accruedInterest = updatedRow.accruedInterest || 0;
  const side = updatedRow.side;

  switch (changedField) {
    case "tradeQuantity":
      // When trade qty changes, recalculate: ending qty, trade value, ending market value
      updatedRow.tradeQuantity = newValue;
      updatedRow.endingQuantity = calculateEndingQty(newValue, currentQty, side);
      updatedRow.tradeValue = calculateTradeValue(newValue, currentPrice, accruedInterest);
      updatedRow.endingSecurityMarketValue = calculateendingSecurityMarketValue(updatedRow.endingQuantity, currentPrice, accruedInterest);
      break;

    case "endingQuantity":
      // When ending qty changes, detect if side needs to flip
      updatedRow.endingQuantity = newValue;
      const endingQtyNum = Number(newValue) || 0;
      
      // Determine the correct side based on ending qty vs current qty
      let newSide = side;
      let calculatedTradeQty;
      
      if (endingQtyNum > currentQty) {
        // Ending qty is more than current, should be BUY
        newSide = "BUY";
        calculatedTradeQty = endingQtyNum - currentQty;
      } else if (endingQtyNum < currentQty) {
        // Ending qty is less than current, should be SELL
        newSide = "SELL";
        calculatedTradeQty = currentQty - endingQtyNum;
      } else {
        // Ending qty equals current, no trade needed
        calculatedTradeQty = 0;
        newSide = side; // Keep original side
      }
      
      // Update side if it changed
      updatedRow.side = newSide;
      updatedRow.tradeQuantity = calculatedTradeQty;
      updatedRow.tradeValue = calculateTradeValue(calculatedTradeQty, currentPrice, accruedInterest);
      updatedRow.endingSecurityMarketValue = calculateendingSecurityMarketValue(newValue, currentPrice, accruedInterest);
      break;

    case "tradeValue":
      // When trade value changes, recalculate: trade qty, ending qty, ending market value
      updatedRow.tradeValue = newValue;
      updatedRow.tradeQuantity = calculateTradeQtyFromValue(newValue, currentPrice, accruedInterest);
      updatedRow.endingQuantity = calculateEndingQty(updatedRow.tradeQuantity, currentQty, side);
      updatedRow.side = getSideFromEndingQty(updatedRow.endingQuantity, currentQty, side);
      updatedRow.endingSecurityMarketValue = calculateendingSecurityMarketValue(updatedRow.endingQuantity, currentPrice, accruedInterest);
      break;

    case "endingSecurityMarketValue":
      // When ending market value changes, recalculate: ending qty, trade qty, trade value
      updatedRow.endingSecurityMarketValue = newValue;
      updatedRow.endingQuantity = calculateEndingQtyFromValue(newValue, currentPrice, accruedInterest);
      updatedRow.side = getSideFromEndingQty(updatedRow.endingQuantity, currentQty, side);
      updatedRow.tradeQuantity = calculateTradeQty(updatedRow.endingQuantity, currentQty, updatedRow.side);
      updatedRow.tradeValue = calculateTradeValue(updatedRow.tradeQuantity, currentPrice, accruedInterest);
      break;

    default:
      // No calculation needed for other fields
      updatedRow[changedField] = newValue;
  }

  return updatedRow;
};

/**
 * Batch update multiple trade rows with calculated fields
 * 
 * Useful for processing multiple trades at once.
 * 
 * @param {Array} tradeRows - Array of trade row objects
 * @param {string} changedField - The field that was changed
 * @param {Array} newValues - Array of new values (one per row)
 * @returns {Array} - Array of updated trade rows
 */
export const batchUpdateTradeRowCalculations = (tradeRows, changedField, newValues) => {
  if (!Array.isArray(tradeRows)) return tradeRows;

  return tradeRows.map((row, index) =>
    updateTradeRowCalculations(row, changedField, newValues[index])
  );
};

/**
 * Validate trade quantity
 * Can be extended for more validation rules
 * 
 * @param {number} tradeQty - Trade quantity to validate
 * @param {number} minQty - Minimum trade quantity (optional)
 * @param {number} lotSize - Lot size constraint (optional)
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export const validateTradeQty = (tradeQty, minQty = 0, lotSize = null) => {
  const tqty = Number(tradeQty);

  if (isNaN(tqty)) {
    return { isValid: false, error: "Trade quantity must be a number" };
  }

  if (tqty < minQty) {
    return { isValid: false, error: `Trade quantity must be at least ${minQty}` };
  }

  if (lotSize && tqty > 0 && tqty % lotSize !== 0) {
    return { isValid: false, error: `Trade quantity must be a multiple of ${lotSize}` };
  }

  return { isValid: true, error: null };
};

/**
 * Validate trade quantity against maximum limits
 * 
 * @param {number} tradeQty - Trade quantity to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export const validateTradeQtyLimit = (tradeQty) => {
  const tqty = Number(tradeQty);

  if (isNaN(tqty)) {
    return { isValid: false, error: "Trade quantity must be a number" };
  }

  if (tqty < TRADE_CONSTANTS.MIN_QUANTITY) {
    return { isValid: false, error: `Trade quantity cannot be negative` };
  }

  if (tqty > TRADE_CONSTANTS.MAX_QUANTITY) {
    return { isValid: false, error: `Trade quantity cannot exceed ${TRADE_CONSTANTS.MAX_QUANTITY.toLocaleString()}` };
  }

  return { isValid: true, error: null };
};

/**
 * Validate trade value against maximum limits
 * 
 * @param {number} tradeValue - Trade value to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export const validateTradeValueLimit = (tradeValue) => {
  const tvalue = Number(tradeValue);

  if (isNaN(tvalue)) {
    return { isValid: false, error: "Trade value must be a number" };
  }

  if (tvalue < TRADE_CONSTANTS.MIN_VALUE) {
    return { isValid: false, error: `Trade value cannot be negative` };
  }

  if (tvalue > TRADE_CONSTANTS.MAX_VALUE) {
    return { isValid: false, error: `Trade value cannot exceed ${TRADE_CONSTANTS.MAX_VALUE.toLocaleString()}` };
  }

  return { isValid: true, error: null };
};

/**
 * Validate trade row (all editable fields)
 * 
 * @param {Object} tradeRow - Trade row to validate
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export const validateTradeRow = (tradeRow) => {
  if (!tradeRow) return { isValid: false, error: "Trade row is required" };

  // Validate quantities
  const tradeQtyValidation = validateTradeQtyLimit(tradeRow.tradeQuantity);
  if (!tradeQtyValidation.isValid) return tradeQtyValidation;

  const endingQtyValidation = validateTradeQtyLimit(tradeRow.endingQuantity);
  if (!endingQtyValidation.isValid) return endingQtyValidation;

  // Validate values
  const tradeValueValidation = validateTradeValueLimit(tradeRow.tradeValue);
  if (!tradeValueValidation.isValid) return tradeValueValidation;

  const endingMktValueValidation = validateTradeValueLimit(tradeRow.endingSecurityMarketValue);
  if (!endingMktValueValidation.isValid) return endingMktValueValidation;

  return { isValid: true, error: null };
};

/**
 * Validate trade row calculation result
 * Checks if calculated values exceed limits
 * 
 * @param {Object} calculatedRow - The calculated trade row
 * @returns {Object} - { isValid: boolean, error: string|null }
 */
export const validateCalculatedRow = (calculatedRow) => {
  // Check quantities
  const tradeQty = Number(calculatedRow.tradeQuantity ?? 0);
  if (Number.isNaN(tradeQty)) {
    return { isValid: false, error: "Calculated trade quantity must be a number" };
  }
  if (tradeQty < TRADE_CONSTANTS.MIN_QUANTITY) {
    return { isValid: false, error: `Calculated trade quantity cannot be negative` };
  }
  if (tradeQty > TRADE_CONSTANTS.MAX_QUANTITY) {
    return { isValid: false, error: `Calculated trade quantity exceeds maximum of ${TRADE_CONSTANTS.MAX_QUANTITY.toLocaleString()}` };
  }

  const endingQty = Number(calculatedRow.endingQuantity ?? 0);
  if (Number.isNaN(endingQty)) {
    return { isValid: false, error: "Calculated ending quantity must be a number" };
  }
  if (endingQty < TRADE_CONSTANTS.MIN_QUANTITY) {
    return { isValid: false, error: `Calculated ending quantity cannot be negative` };
  }
  if (endingQty > TRADE_CONSTANTS.MAX_QUANTITY) {
    return { isValid: false, error: `Calculated ending quantity exceeds maximum of ${TRADE_CONSTANTS.MAX_QUANTITY.toLocaleString()}` };
  }

  const tradeValue = Number(calculatedRow.tradeValue ?? 0);
  if (Number.isNaN(tradeValue)) {
    return { isValid: false, error: "Calculated trade value must be a number" };
  }
  if (tradeValue < TRADE_CONSTANTS.MIN_VALUE) {
    return { isValid: false, error: `Calculated trade value cannot be negative` };
  }
  if (tradeValue > TRADE_CONSTANTS.MAX_VALUE) {
    return { isValid: false, error: `Calculated trade value exceeds maximum of ${TRADE_CONSTANTS.MAX_VALUE.toLocaleString()}` };
  }

  const endingSecurityMarketValue = Number(calculatedRow.endingSecurityMarketValue ?? 0);
  if (Number.isNaN(endingSecurityMarketValue)) {
    return { isValid: false, error: "Calculated ending market value must be a number" };
  }
  if (endingSecurityMarketValue < TRADE_CONSTANTS.MIN_VALUE) {
    return { isValid: false, error: `Calculated ending market value cannot be negative` };
  }
  if (endingSecurityMarketValue > TRADE_CONSTANTS.MAX_VALUE) {
    return { isValid: false, error: `Calculated ending market value exceeds maximum of ${TRADE_CONSTANTS.MAX_VALUE.toLocaleString()}` };
  }

  return { isValid: true, error: null };
};

/**
 * Recalculate trade-related values for an entire trade list
 * (Used when current qty or other base values change)
 * 
 * @param {Array} tradeList - Array of trade rows
 * @param {string} reason - Reason for recalculation (e.g., 'currentQtyChanged', 'sideChanged')
 * @returns {Array} - Updated trade list
 */
export const recalculateTradeList = (tradeList, reason = "unknown") => {
  if (!Array.isArray(tradeList)) return tradeList;

  return tradeList.map((trade) => {
    // When current qty changes, recalculate ending qty based on existing trade qty
    if (reason === "currentQtyChanged") {
      return updateTradeRowCalculations(trade, "tradeQuantity", trade.tradeQuantity);
    }
    return trade;
  });
};

const getSideFromEndingQty = (endingQty, currentQty, fallbackSide) => {
  if (endingQty > currentQty) return "BUY";
  if (endingQty < currentQty) return "SELL";
  return fallbackSide;
};
